<script setup lang="ts">
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { requiredValidator } from "@validators";
import { VForm } from "vuetify/components";
import type { BookingStepProps } from "../types/index";

const props = defineProps<
  BookingStepProps & {
    timeList?: any[];
    durationList?: any[];
    datePickerConfig?: any;
    memberList?: any[];
    areaList?: any[];
    getAreaList?: any[];
  }
>();

const emit = defineEmits<{
  (e: "update:form", value: any): void;
  (e: "validate"): Promise<boolean>;
  (e: "updateErrorMessage", value: any): void;
  (e: "loadUsersList", value: boolean): void;
}>();
const userStore = useUserStore();
const errorMessage = ref("");
const dateTimeFormRef = ref<InstanceType<typeof VForm>>();
const isLeftHand = ref(false);

const filteredAreaList = ref<any[]>([]);
const selectedTime = ref<any>({
  value: null,
  areaId: null,
});
const { currentLocationDetails } = storeToRefs(userStore);
// Computed
const selectedActivity = computed(() =>
  props.activityList?.find((el) => el.id === props.form.activity),
);

const getBookingSpacing = computed(
  () => selectedActivity.value?.bookingSpacing || 60,
);

const showLeftHand = computed(() => {
  // Only show left hand option if some areas don't support left-handed players
  return props.getAreaList?.some((area) => !area.canLeftHanded);
});

const times = ref<any[]>([]);

const getDurationList = computed(() => {
  if (!props.form.activity) return [];

  const durationList = [];
  const timeFormat = getBookingSpacing.value % 60 === 0 ? "H" : "H:mm";
  const numberOfSlots = Math.min(4, 240 / getBookingSpacing.value);
  const startTime = moment()
    .startOf("day")
    .add(getBookingSpacing.value, "minutes");

  for (let i = 0; i < numberOfSlots; i++) {
    const timeSlot = startTime
      .clone()
      .add(i * getBookingSpacing.value, "minutes")
      .format(timeFormat);
    durationList.push({
      value: Number(timeSlot),
      text: Number(timeSlot),
    });
  }
  return durationList;
});

const getBookingList = async (date: any) => {
  const _date =
    typeof date === "string" ? date : moment(date).format("DD-MM-YYYY");
  const nzStartDate = moment
    .tz(_date, "DD-MM-YYYY", currentLocationDetails.value.timeZoneCode)
    .startOf("day")
    .utc()
    .format("YYYY-MM-DD HH:mm:ss");
  const nzEndDate = moment
    .tz(_date, "DD-MM-YYYY", currentLocationDetails.value.timeZoneCode)
    .endOf("day")
    .utc()
    .format("YYYY-MM-DD HH:mm:ss");
  // Get all bookings from today onwards
  const bookings = await pb.collection("bookings").getFullList({
    filter: `startTime >= "${nzStartDate}" && startTime <= "${nzEndDate}"`,
  });
  filteredAreaList.value = [...(props.getAreaList || [])];
  // Process each area in the list
  filteredAreaList.value.forEach((area: any) => {
    // Filter bookings for this specific area
    const areaBookings = bookings.filter((book: any) => book.area === area.id);

    // Initialize timeSlot array for this area
    area.timeSlot = [];

    // Process each booking for this area
    areaBookings.forEach((book: any) => {
      const startTime = moment(book.startTime);
      const bookingSpacing = selectedActivity.value?.bookingSpacing || 60; // Default to 60 minutes if due is not set
      const slots = Math.ceil(book.duration / bookingSpacing);

      // Generate time slots for the booking duration
      for (let i = 0; i < slots; i++) {
        const slotTime = startTime
          .clone()
          .add(i * bookingSpacing, "minutes")
          .format("HH:mm");
        area.timeSlot.push(slotTime);
      }
    });
  });
};

onMounted(() => {
  // Run getBookingList on component mount
  getBookingList(props.form.date);
});

const debounce = (fn: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

// Create debounced version of getBookingList
const debouncedGetBookingList = debounce(getBookingList, 300);

const handleDateChange = (date: any) => {
  selectedTime.value = {
    value: null,
    areaId: null,
  };
  emit("update:form", {
    ...props.form,
    date: date,
    time: null,
    selectedOptions: [],
  });
  debouncedGetBookingList(date);
};

const handleDurationChange = (duration: any) => {
  selectedTime.value = {
    value: null,
    areaId: null,
  };
  if (duration && duration.value) {
    emit("update:form", {
      ...props.form,
      duration: duration.value,
      time: null,
    });
  } else {
    emit("update:form", {
      ...props.form,
      duration: duration,
      time: null,
      selectedOptions: [],
    });
  }
};

const handleTimeChange = (time: any) => {
  selectedTime.value = time;
  emit("update:form", {
    ...props.form,
    time: time.value,
    area: time.areaId,
    selectedOptions: [],
  });
};

// Watch for changes in relevant dependencies
watch(
  [
    () => props.form.date,
    () => props.form.duration,
    () => isLeftHand.value,
    () => filteredAreaList.value,
    () => props.form.activity,
  ],
  async () => {
    times.value = [];
    if (!props.form.date) return;
    const currentDate = moment()
      .tz(currentLocationDetails.value.timeZoneCode)
      .startOf("day");
    const selectedDate = moment(props.form.date, "DD-MM-YYYY").startOf("day");

    if (selectedDate.isBefore(currentDate)) {
      times.value = [];
      return;
    }

    const interval = getBookingSpacing.value;
    const isToday = moment(props.form.date, "DD-MM-YYYY")
      .startOf("day")
      .isSame(moment().startOf("day"), "day");

    const formatTimeTo12Hour = (hour: any, min: any) => {
      return moment(`${hour}:${min}`, "HH:mm").format("hh:mm A");
    };

    const dayName = props.form.date
      ? moment(props.form.date, "DD-MM-YYYY").format("dddd").toLowerCase()
      : "sunday";
    const filteredTimes = (
      selectedActivity.value?.expand?.availibleTimes || []
    )?.filter((timeSlot: any) => timeSlot[dayName] && timeSlot.allUsers);

    // Create available times for all areas
    const areaAvailability: { areaId: string; availableTimes: string[] }[] = [];

    const areaList = filteredAreaList.value?.filter((el: any) => {
      if (isLeftHand.value === true) {
        return el.canLeftHanded;
      } else {
        return true;
      }
    });
    // Calculate available times for each area
    (areaList || []).forEach((area: any) => {
      const availableTimesForArea = [];

      // Update buffer minutes logic
      const bufferMinutes = 30;
      const currentTimeInMinutes = isToday
        ? moment().tz(currentLocationDetails.value.timeZoneCode).hours() * 60 +
          moment().tz(currentLocationDetails.value.timeZoneCode).minutes() +
          bufferMinutes
        : 0;

      // Generate all possible time slots
      for (let hour = 0; hour < 24; hour++) {
        for (let min = 0; min < 60; min += interval) {
          const formattedHour = hour.toString().padStart(2, "0");
          const formattedMin = min.toString().padStart(2, "0");
          const time = `${formattedHour}:${formattedMin}`;

          const timeInMinutes = hour * 60 + min;

          // Skip past times including buffer
          if (isToday && timeInMinutes <= currentTimeInMinutes) {
            continue;
          }

          // Check if the time slot is available in activity hours
          const isAvailable = filteredTimes.some((timeSlot: any) => {
            const startTime = timeSlot.startHour * 60 + timeSlot.startMin;
            const endTime = timeSlot.finishHour * 60 + timeSlot.finishMin;

            if (startTime === 0 && endTime === 0) return true;
            else if (startTime === 0) return timeInMinutes < endTime;
            else if (endTime === 0) return timeInMinutes >= startTime;

            if (endTime > startTime) {
              return timeInMinutes >= startTime && timeInMinutes < endTime;
            }
            if (endTime <= startTime) {
              return timeInMinutes >= startTime || timeInMinutes < endTime;
            }
            return false;
          });
          // Check if time is not in area.timeSlot
          if (isAvailable && area.timeSlot && !area.timeSlot?.includes(time)) {
            availableTimesForArea.push(time);
          }
        }
      }

      areaAvailability.push({
        areaId: area.id,
        availableTimes: availableTimesForArea,
      });
    });

    // Find area with most available slots
    if (areaAvailability.length > 0) {
      // First collect all available time slots with their areas
      const allTimeSlots: { time: string; areaId: string }[] = [];

      // Process each area's available times
      areaAvailability.forEach(({ areaId, availableTimes }) => {
        const duration = Number(props.form.duration);

        // Filter times based on duration for this area
        const filteredTimes = availableTimes.filter((time) => {
          if (duration <= 1) return true;

          // Check consecutive slots
          const [hour, minute] = time.split(":").map(Number);
          const currentTimeInMinutes = hour * 60 + minute;

          // Verify all required consecutive slots
          for (let i = 1; i < duration; i++) {
            const nextTimeInMinutes = currentTimeInMinutes + i * 60;
            const nextHour = Math.floor(nextTimeInMinutes / 60);
            const nextMinute = nextTimeInMinutes % 60;
            const nextTimeStr = `${nextHour
              .toString()
              .padStart(2, "0")}:${nextMinute.toString().padStart(2, "0")}`;

            if (!availableTimes.includes(nextTimeStr)) {
              return false;
            }
          }
          return true;
        });

        // Add each filtered time with its area to allTimeSlots
        filteredTimes.forEach((time) => {
          allTimeSlots.push({ time, areaId });
        });
      });

      // Group time slots by time
      const timeGroups = allTimeSlots.reduce(
        (groups, slot) => {
          if (!groups[slot.time]) {
            groups[slot.time] = [];
          }
          groups[slot.time].push(slot.areaId);
          return groups;
        },
        {} as Record<string, string[]>,
      );

      // Create final times array with unique times and random areas
      times.value = Object.entries(timeGroups).map(([time, areas]) => {
        const [hour, minute] = time.split(":");
        const randomAreaId = areas[Math.floor(Math.random() * areas.length)];

        return {
          label: formatTimeTo12Hour(hour, minute),
          value: time,
          isNormalHour: true,
          areaId: randomAreaId,
        };
      });

      // Sort times chronologically
      times.value.sort((a, b) => a.value.localeCompare(b.value));
    }
  },
  { immediate: true, deep: false },
);

watch(
  () => props.form.time,
  () => {
    if (props.form.time) {
      errorMessage.value = "";
    }
  },
);

defineExpose({
  validate: async () => {
    // Add time validation check
    if (!selectedTime.value.value) {
      emit("updateErrorMessage", {
        isVisible: true,
        message: "Please select a booking time",
      });
      return false;
    }
    const results = await dateTimeFormRef.value?.validate();
    return results?.valid ?? false;
  },
});
</script>

<template>
  <VForm ref="dateTimeFormRef">
    <VRow>
      <!-- Date Picker -->
      <VCol lg="3" md="3" sm="4" xs="6" cols="6">
        <AppDateTimePicker
          v-model="form.date"
          :config="datePickerConfig"
          :disabled="isFieldsDisabled"
          :rules="[requiredValidator]"
          label="Date"
          placeholder="Select Date"
          @update:modelValue="handleDateChange"
        />
      </VCol>

      <!-- Duration Select -->
      <VCol lg="2" md="2" sm="4" xs="6" cols="6">
        <VCombobox
          v-model="form.duration"
          :items="getDurationList"
          v-if="getDurationList.length"
          :rules="[requiredValidator]"
          :menu-props="{ eager: true }"
          item-title="text"
          item-value="value"
          label="Duration"
          @update:modelValue="handleDurationChange($event)"
        />
      </VCol>

      <VCol lg="5" md="5" sm="6" xs="6" cols="6">
        Booking durations are in hours but actual play time is 5 mins less for
        changeover
      </VCol>

      <VCol lg="6" md="6" sm="6" xs="6" cols="6" v-if="showLeftHand">
        <VCheckbox
          v-model="isLeftHand"
          label="Left handed player in group"
          color="primary"
          hide-details
          @update:modelValue="
            const area = props.areaList?.find((el) => el.id === form.area);
          "
        />
      </VCol>

      <!-- Time Select -->
      <VCol cols="12">
        <div v-if="times.length !== 0">
          <p>Choose from Available Booking Times:</p>
        </div>
        <div class="time-buttons-wrapper">
          <VBtn
            v-for="time in times"
            :key="time.value"
            class="time-btn"
            :color="selectedTime.value === time.value ? 'primary' : 'secondary'"
            @click="handleTimeChange(time)"
          >
            {{ time.label }}
          </VBtn>
        </div>
        <div v-if="times.length === 0" class="error-message">
          <VIcon icon="mdi-alert-circle" />
          No bookings available for the selected date.
        </div>
        <div v-if="errorMessage" style="color: red; font-size: 12px">
          {{ errorMessage }}
        </div>
      </VCol>
    </VRow>
  </VForm>
</template>

<style lang="scss" scoped>
.member_list_selection {
  :deep(.v-field__input) {
    max-block-size: 7.5rem;
    overflow-y: auto;
    scrollbar-width: thin;
  }
}

.time-buttons-wrapper {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  inline-size: 100%;
}

.time-btn {
  inline-size: 100%;
  min-inline-size: unset;
}
</style>
